import React from 'react';
import { Header, Form, Input, Button } from 'semantic-ui-react';
import { getCanvas } from 'src/utils/mapUtils';
import { states } from '../map/leafletCanvas';
import LayerList from '../mainframe/LayerList';
import {
  CHANNEL_1_COLOR,
  CHANNEL_2_COLOR,
  CHANNEL_3_COLOR,
  CHANNEL_4_COLOR,
  CHANNEL_AUTO_COLOR,
} from '../map/apFeatures';
import { set60GHzEquipment, setApScale } from '../map/map.reducer';
import { useSelector } from 'react-redux';
import { store } from 'src/store';
import { PanelHeading } from 'src/components/PanelHeading';

const channelLegendData = [
  { name: 'Auto', color: CHANNEL_AUTO_COLOR },
  { name: '58.32 GHz (Channel 1)', color: CHANNEL_1_COLOR },
  { name: '60.48 GHz (Channel 2)', color: CHANNEL_2_COLOR },
  { name: '62.64 GHz (Channel 3)', color: CHANNEL_3_COLOR },
  { name: '64.80 GHz (Channel 4)', color: CHANNEL_4_COLOR },
];

const polarityLegendData = ['Auto', 'Even', 'Odd'];

export function ChannelPlan60() {
  const { apScale, channel60GHzChoice } = useSelector((state) => state.map);
  const mapState = useSelector((state) => state.mainFrame.mapState);

  const editMode = mapState === states.MAP_60GHZ_CHANNEL_PLAN_EDIT;

  const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value)) {
      value = 0;
    }
    store.dispatch(setApScale(value));
    getCanvas().setApScale(value);
  };

  const toggleChannelPlanning = () => {
    if (mapState == states.MAP_60GHZ_CHANNEL_PLAN_EDIT) {
      getCanvas().resetState();
    } else {
      getCanvas().channelPlanEditing();
    }
  };

  const handleEquipmentToggle = (
    type: 'channel' | 'polarity',
    value: string
  ) => {
    const currentValue = channel60GHzChoice[type];
    const otherType = type === 'channel' ? 'polarity' : 'channel';
    let equipment = { channel: 'Auto', polarity: null };
    if (currentValue !== value) {
      // Update the specific type while preserving the other
      equipment[otherType] = null;
      equipment[type] = value;
    }
    store.dispatch(set60GHzEquipment(equipment));
  };

  return (
    <div className="panel-with-heading channel-plan-60-panel">
      <PanelHeading title="60 GHz Channel Planning" />
      <div className="main-panel">
        <Header as="h2">Equipment</Header>
        {/* Channel Legend */}
        <div style={{ marginBottom: '1.5em' }}>
          <Header as="h4">Channels</Header>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}
          >
            {/* height: 19px prevents layout shift */}
            {channelLegendData.map((channel) => (
              <div
                key={channel.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5em',
                  height: '19px',
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    backgroundColor: channel.color,
                    border: '1px solid #ccc',
                  }}
                ></div>
                <span style={{ flex: 1 }}>{channel.name}</span>
                {editMode && (
                  <Button
                    size="mini"
                    color={
                      channel60GHzChoice.channel === channel.name
                        ? 'green'
                        : 'blue'
                    }
                    onClick={() =>
                      handleEquipmentToggle('channel', channel.name)
                    }
                  >
                    {channel60GHzChoice.channel === channel.name
                      ? 'Selected'
                      : 'Select'}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <Header as="h4" style={{ marginTop: '1em' }}>
            Polarity
          </Header>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}
          >
            {polarityLegendData.map((polarity) => (
              <div
                key={polarity}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5em',
                  height: '19px',
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    textAlign: 'center',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    fontWeight: 'bold',
                    border: '1px solid #000',
                  }}
                >
                  {polarity === 'Auto' ? 'A' : polarity === 'Even' ? 'E' : 'O'}
                </div>
                <span style={{ flex: 1 }}>{polarity}</span>
                {editMode && (
                  <Button
                    size="mini"
                    color={
                      channel60GHzChoice.polarity === polarity
                        ? 'green'
                        : 'blue'
                    }
                    onClick={() => handleEquipmentToggle('polarity', polarity)}
                  >
                    {channel60GHzChoice.polarity === polarity
                      ? 'Selected'
                      : 'Select'}
                  </Button>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: '1em' }}>
            <Button
              onClick={toggleChannelPlanning}
              color={editMode ? 'green' : null}
              primary={!editMode}
              size="medium"
              style={{ width: '100%' }}
            >
              {editMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
            </Button>
          </div>
        </div>
        <Header as="h2">Display</Header>
        <Form>
          <label style={{ marginRight: '1em' }}>Network Device Scale</label>
          <Form.Field
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              columnGap: '1em',
            }}
          >
            <Input
              type="range"
              min="5"
              max="100"
              step="5"
              value={apScale}
              onChange={handlePercentageChange}
              style={{ flex: '1 1 0%' }}
            />
            {apScale} %
          </Form.Field>
        </Form>
        <Header as="h3" style={{ marginTop: '1.5em' }}>
          Layers
        </Header>
        <LayerList />
      </div>
    </div>
  );
}
